"use client";

import { PenLine } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import Link from "@/components/ui/link";
import { cn } from "@/lib/utils";

type ReviewWriteButtonProps = {
  canReview: boolean;
  writeHref: string;
  writeLabel: string;
  blockedReason: string;
  isAuthenticated: boolean;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
};

export function ReviewWriteButton({
  canReview,
  writeHref,
  writeLabel,
  blockedReason,
  isAuthenticated,
  className,
  size = "default",
}: ReviewWriteButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const buttonContent = (
    <>
      <PenLine className="h-4 w-4" />
      {writeLabel}
    </>
  );

  return (
    <>
      {canReview ? (
        <Button
          asChild
          size={size}
          className={cn("rounded-full active:scale-[0.98]", className)}
        >
          <Link href={writeHref}>{buttonContent}</Link>
        </Button>
      ) : (
        <Button
          type="button"
          size={size}
          className={cn("rounded-full active:scale-[0.98]", className)}
          onClick={() => setIsOpen(true)}
        >
          {buttonContent}
        </Button>
      )}

      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent
          overlayClassName="!z-[1000]"
          className="!z-[1001] mx-auto max-w-[640px] rounded-t-[28px] border-border bg-surface pb-[max(env(safe-area-inset-bottom),2rem)] text-text-primary shadow-xl dark:border-white/[0.12] dark:bg-[#0b0b0f]/85 dark:text-white"
        >
          <DrawerHeader className="px-5 text-left">
            <DrawerTitle className="text-xl font-semibold text-text-primary dark:text-white">
              Review Eligibility
            </DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-5 py-2">
            <p className="text-sm leading-relaxed text-text-secondary dark:text-white/60">
              {blockedReason}
            </p>
            <div className="flex flex-col gap-2 pt-2">
              {!isAuthenticated ? (
                <Button asChild className="h-auto w-full rounded-full py-3">
                  <Link
                    href={`/sign-in?next=${encodeURIComponent(
                      typeof window !== "undefined"
                        ? window.location.pathname + window.location.search
                        : "",
                    )}`}
                  >
                    Sign in
                  </Link>
                </Button>
              ) : null}
              <DrawerClose asChild>
                <Button
                  variant="outline"
                  className="h-auto w-full rounded-full py-3"
                >
                  Close
                </Button>
              </DrawerClose>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
